/* FamilyPlay — Rainy Sunday games hub for kids. Pick equipment → get 6 games. */
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Loader2, RefreshCw, Sparkles, ArrowLeft, Users, Clock, Heart, Trash2, Wrench, Baby, Book, Settings, Trophy, Plus, X, Check,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function GameCard({ game, onFavorite, favorited, onPlayed, kids }) {
  const [playPickerOpen, setPlayPickerOpen] = useState(false);
  const [selKids, setSelKids] = useState([]);

  const doPlayed = () => {
    if (selKids.length === 0) return;
    onPlayed(game, selKids);
    setPlayPickerOpen(false);
    setSelKids([]);
  };

  return (
    <div className="rounded-2xl border-2 border-orange-100 bg-white p-4 hover:border-orange-300 transition group">
      <div className="flex items-start justify-between gap-2">
        <div className="text-lg font-bold text-orange-900">{game.title}</div>
        <button
          type="button"
          onClick={onFavorite}
          className={`p-1 rounded-full transition ${
            favorited ? 'text-rose-500' : 'text-slate-300 hover:text-rose-500'
          }`}
          data-testid={`game-fav-${game.id}`}
          aria-label="Spara som favorit"
        >
          <Heart className={`w-4 h-4 ${favorited ? 'fill-current' : ''}`} />
        </button>
      </div>
      <div className="flex flex-wrap gap-1.5 mt-2 mb-3">
        {game.age && (
          <span className="text-[10px] bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5 text-amber-800 inline-flex items-center gap-0.5">
            <Baby className="w-2.5 h-2.5" /> {game.age}
          </span>
        )}
        {game.duration && (
          <span className="text-[10px] bg-sky-50 border border-sky-200 rounded-full px-2 py-0.5 text-sky-800 inline-flex items-center gap-0.5">
            <Clock className="w-2.5 h-2.5" /> {game.duration}
          </span>
        )}
        {game.participants && (
          <span className="text-[10px] bg-violet-50 border border-violet-200 rounded-full px-2 py-0.5 text-violet-800 inline-flex items-center gap-0.5">
            <Users className="w-2.5 h-2.5" /> {game.participants}
          </span>
        )}
        {game.skill && (
          <span className="text-[10px] bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5 text-emerald-800">
            🌱 {game.skill}
          </span>
        )}
      </div>
      <div className="space-y-2 text-sm">
        {game.equipment && (
          <div>
            <div className="text-[10px] uppercase font-bold text-orange-600 tracking-wider mb-0.5 flex items-center gap-1">
              <Wrench className="w-3 h-3" /> Utrustning
            </div>
            <p className="text-slate-700">{game.equipment}</p>
          </div>
        )}
        {game.instructions && (
          <div>
            <div className="text-[10px] uppercase font-bold text-orange-600 tracking-wider mb-0.5 flex items-center gap-1">
              <Book className="w-3 h-3" /> Så leker ni
            </div>
            <p className="text-slate-700 leading-relaxed">{game.instructions}</p>
          </div>
        )}
        {game.tip && (
          <div className="rounded-lg bg-amber-50 border border-amber-100 p-2 text-[12px] text-amber-900">
            💡 <b>Tips:</b> {game.tip}
          </div>
        )}
      </div>
      {kids && kids.length > 0 && (
        <div className="mt-3 pt-3 border-t border-orange-50">
          {!playPickerOpen ? (
            <button
              type="button"
              onClick={() => setPlayPickerOpen(true)}
              data-testid={`game-played-${game.id}`}
              className="w-full inline-flex items-center justify-center gap-1 px-3 py-1.5 rounded-full bg-gradient-to-r from-orange-400 to-amber-400 text-white text-xs font-semibold hover:opacity-90"
            >
              <Check className="w-3 h-3" /> Vi lekte den!
            </button>
          ) : (
            <div className="space-y-2">
              <div className="text-[11px] font-medium text-slate-700">Vilka lekte med?</div>
              <div className="flex flex-wrap gap-1.5">
                {kids.map((k) => {
                  const chosen = selKids.includes(k.id);
                  return (
                    <button
                      key={k.id}
                      type="button"
                      onClick={() => setSelKids((s) => (chosen ? s.filter((x) => x !== k.id) : [...s, k.id]))}
                      data-testid={`game-played-kid-${k.id}`}
                      className={`text-xs px-2 py-1 rounded-full border ${
                        chosen
                          ? 'bg-orange-500 text-white border-orange-500'
                          : 'bg-white text-slate-700 border-slate-200 hover:border-orange-300'
                      }`}
                    >
                      {chosen ? '✓ ' : ''}{k.name} ({k.age})
                    </button>
                  );
                })}
              </div>
              <div className="flex gap-1.5">
                <button
                  type="button"
                  onClick={doPlayed}
                  disabled={selKids.length === 0}
                  data-testid={`game-played-confirm-${game.id}`}
                  className="flex-1 text-xs px-3 py-1.5 rounded-full bg-orange-500 hover:bg-orange-600 text-white font-medium disabled:opacity-50"
                >
                  Ge {selKids.length * 10} poäng 🏆
                </button>
                <button
                  type="button"
                  onClick={() => { setPlayPickerOpen(false); setSelKids([]); }}
                  className="text-xs px-3 py-1.5 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-700"
                >
                  Avbryt
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function FamilyPlay() {
  const [categories, setCategories] = useState([]);
  const [selected, setSelected] = useState(null);
  const [games, setGames] = useState([]);
  const [loading, setLoading] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [favorites, setFavorites] = useState([]);
  const [showFavorites, setShowFavorites] = useState(false);
  const [kids, setKids] = useState([]);
  const [showKidsSettings, setShowKidsSettings] = useState(false);
  const [scoreboard, setScoreboard] = useState({ scores: [], total_games: 0 });

  const loadFavs = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/family-play/favorites`, { withCredentials: true });
      setFavorites(data.items || []);
    } catch { /* noop */ }
  }, []);

  const loadKids = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/family-play/kids`, { withCredentials: true });
      setKids(data.kids || []);
    } catch { /* noop */ }
  }, []);

  const loadScoreboard = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/family-play/played/recent?days=7`, { withCredentials: true });
      setScoreboard({ scores: data.scores || [], total_games: data.total_games || 0 });
    } catch { /* noop */ }
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/family-play/categories`, { withCredentials: true });
        setCategories(data.categories || []);
      } catch { /* noop */ }
      await loadFavs();
      await loadKids();
      await loadScoreboard();
    })();
  }, [loadFavs, loadKids, loadScoreboard]);

  const markPlayed = async (game, kidIds) => {
    if (!selected) return;
    try {
      await axios.post(
        `${API}/family-play/played`,
        { category: selected.id, game, kid_ids: kidIds },
        { withCredentials: true },
      );
      toast.success(`+${kidIds.length * 10} poäng till familjen 🏆`);
      loadScoreboard();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Kunde inte spara');
    }
  };

  const pickCategory = async (cat) => {
    setSelected(cat);
    setGames([]);
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/family-play/games?category=${cat.id}`, { withCredentials: true, timeout: 90000 });
      setGames(data.games || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Kunde inte hämta lekar');
    } finally { setLoading(false); }
  };

  const regenerate = async () => {
    if (!selected) return;
    setRegenerating(true);
    try {
      const { data } = await axios.post(
        `${API}/family-play/regenerate`,
        { category: selected.id },
        { withCredentials: true, timeout: 90000 },
      );
      setGames(data.games || []);
      toast.success('Nya lekar klara ✨');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Kunde inte generera');
    } finally { setRegenerating(false); }
  };

  const favorite = async (game) => {
    const already = favorites.find((f) => f.game?.title === game.title);
    if (already) {
      try {
        await axios.delete(`${API}/family-play/favorites/${already.id}`, { withCredentials: true });
        setFavorites((fs) => fs.filter((f) => f.id !== already.id));
        toast('Borttagen från favoriter');
      } catch { /* noop */ }
      return;
    }
    try {
      const { data } = await axios.post(
        `${API}/family-play/favorites`,
        { category: selected.id, game },
        { withCredentials: true },
      );
      setFavorites((fs) => [{ id: data.id, category: selected.id, game, created_at: new Date().toISOString() }, ...fs]);
      toast('Sparad som favorit 💜');
    } catch { toast.error('Kunde inte spara'); }
  };

  const isFavorited = (game) => !!favorites.find((f) => f.game?.title === game.title);
  const removeFavorite = async (id) => {
    try {
      await axios.delete(`${API}/family-play/favorites/${id}`, { withCredentials: true });
      setFavorites((fs) => fs.filter((f) => f.id !== id));
    } catch { toast.error('Kunde inte ta bort'); }
  };

  // Category picker view
  if (!selected) {
    return (
      <div className="max-w-4xl mx-auto space-y-4" data-testid="family-play-page">
        {/* Hero */}
        <div className="rounded-3xl p-5 bg-gradient-to-br from-orange-100 via-amber-100 to-rose-100 border border-orange-200 relative overflow-hidden">
          <div className="absolute -right-4 -top-4 opacity-15 text-8xl leading-none pointer-events-none">🌧</div>
          <div className="text-[11px] uppercase font-semibold tracking-wider text-orange-800/80">Lek-labbet</div>
          <h1 className="text-2xl font-bold text-orange-900" style={{ fontFamily: 'ui-serif, Georgia, serif' }}>
            Regnig söndag med barnen? 🌧☔
          </h1>
          <p className="text-sm text-orange-900/80 mt-1">
            Välj vilken utrustning ni har hemma → jag ger 6 roliga lekar och experiment att testa direkt.
          </p>
          {favorites.length > 0 && (
            <button
              type="button"
              onClick={() => setShowFavorites(true)}
              data-testid="family-play-show-favs"
              className="mt-3 inline-flex items-center gap-1 px-3 py-1 rounded-full bg-white/70 border border-rose-300 text-rose-800 text-xs font-medium hover:bg-white"
            >
              <Heart className="w-3 h-3 fill-current" /> Mina favoriter ({favorites.length})
            </button>
          )}
          <button
            type="button"
            onClick={() => setShowKidsSettings(true)}
            data-testid="family-play-kids-settings"
            className="mt-3 ml-2 inline-flex items-center gap-1 px-3 py-1 rounded-full bg-white/70 border border-orange-300 text-orange-800 text-xs font-medium hover:bg-white"
          >
            <Settings className="w-3 h-3" /> {kids.length > 0 ? `Barn (${kids.length})` : 'Lägg till barn'}
          </button>
        </div>

        {/* Weekly scoreboard */}
        {kids.length > 0 && scoreboard.total_games > 0 && (
          <div className="rounded-2xl p-4 bg-gradient-to-br from-amber-50 to-orange-50 border border-amber-200" data-testid="family-play-scoreboard">
            <div className="text-sm font-semibold text-amber-900 flex items-center gap-1.5 mb-2">
              <Trophy className="w-4 h-4 text-amber-600" /> Veckans familjetävling
              <span className="ml-auto text-[11px] text-amber-700 font-normal">{scoreboard.total_games} {scoreboard.total_games === 1 ? 'lek' : 'lekar'} denna vecka</span>
            </div>
            <div className="space-y-1.5">
              {scoreboard.scores.map((k, i) => {
                const max = Math.max(1, ...scoreboard.scores.map((s) => s.points));
                return (
                  <div key={k.id} className="flex items-center gap-2">
                    <div className="w-6 text-sm">{i === 0 && k.points > 0 ? '🥇' : i === 1 && k.points > 0 ? '🥈' : i === 2 && k.points > 0 ? '🥉' : '👦'}</div>
                    <div className="text-sm text-slate-800 w-24 truncate">{k.name} <span className="text-[10px] text-slate-400">({k.age})</span></div>
                    <div className="flex-1 h-2 bg-white rounded-full overflow-hidden border border-amber-100">
                      <div className="h-full bg-gradient-to-r from-amber-400 to-orange-500" style={{ width: `${(k.points / max) * 100}%` }} />
                    </div>
                    <div className="text-sm font-bold text-amber-800 w-12 text-right">{k.points}p</div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Category grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {categories.map((c) => (
            <button
              key={c.id}
              type="button"
              onClick={() => pickCategory(c)}
              data-testid={`family-play-cat-${c.id}`}
              className="text-left rounded-2xl p-4 bg-white border-2 border-orange-100 hover:border-orange-300 hover:shadow-md transition"
            >
              <div className="text-4xl mb-2">{c.emoji}</div>
              <div className="font-bold text-slate-900">{c.label}</div>
              <div className="text-[11px] text-slate-500 mt-0.5">{c.sub}</div>
            </button>
          ))}
        </div>

        {/* Favorites modal */}
        {showKidsSettings && (
          <KidsSettingsModal
            initial={kids}
            onClose={() => setShowKidsSettings(false)}
            onSaved={(newKids) => { setKids(newKids); setShowKidsSettings(false); toast('Barn sparade — nya lekar anpassas efter deras åldrar 💜'); }}
          />
        )}

        {showFavorites && (
          <div className="fixed inset-0 z-[100] bg-black/50 flex items-center justify-center p-4" onClick={() => setShowFavorites(false)}>
            <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
              <div className="sticky top-0 bg-white border-b border-slate-100 p-4 flex items-center justify-between">
                <div className="font-bold text-slate-900 flex items-center gap-1.5">
                  <Heart className="w-4 h-4 text-rose-500 fill-current" /> Mina favorit-lekar
                </div>
                <button type="button" onClick={() => setShowFavorites(false)} className="text-slate-400 hover:text-slate-700">
                  <ArrowLeft className="w-4 h-4" />
                </button>
              </div>
              <div className="p-4 space-y-3">
                {favorites.length === 0 ? (
                  <p className="text-sm text-slate-500 italic">Inga favoriter än — spara med 💜-ikonen på en lek.</p>
                ) : favorites.map((f) => (
                  <div key={f.id} className="rounded-xl border border-slate-200 p-3 relative group">
                    <div className="text-sm font-bold text-slate-900">{f.game?.title}</div>
                    <div className="text-[11px] text-slate-500 mt-0.5">
                      {(categories.find((c) => c.id === f.category) || {}).emoji}{' '}
                      {(categories.find((c) => c.id === f.category) || {}).label}
                    </div>
                    <p className="text-xs text-slate-700 mt-1">{f.game?.instructions?.slice(0, 200)}</p>
                    <button
                      type="button"
                      onClick={() => removeFavorite(f.id)}
                      className="absolute top-2 right-2 text-slate-300 hover:text-rose-500 opacity-0 group-hover:opacity-100"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // Games list view
  return (
    <div className="max-w-4xl mx-auto space-y-4" data-testid="family-play-games">
      <div className="rounded-3xl p-4 bg-gradient-to-br from-orange-100 via-amber-100 to-rose-100 border border-orange-200 flex items-start justify-between gap-3">
        <div>
          <button
            type="button"
            onClick={() => { setSelected(null); setGames([]); }}
            data-testid="family-play-back"
            className="inline-flex items-center gap-1 text-xs text-orange-700 hover:text-orange-900 mb-1"
          >
            <ArrowLeft className="w-3 h-3" /> Alla kategorier
          </button>
          <h1 className="text-2xl font-bold text-orange-900 flex items-center gap-2">
            <span className="text-3xl">{selected.emoji}</span> {selected.label}
          </h1>
          <p className="text-xs text-orange-800/70 mt-0.5">{selected.sub}</p>
        </div>
        <button
          type="button"
          onClick={regenerate}
          disabled={regenerating}
          data-testid="family-play-regenerate"
          className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-white/80 hover:bg-white border border-orange-300 text-orange-800 text-xs font-semibold disabled:opacity-50"
        >
          {regenerating ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
          Nya lekar
        </button>
      </div>

      {loading ? (
        <div className="text-center py-12 text-slate-400">
          <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" />
          <p className="text-sm">Hittar roliga lekar för {selected.label.toLowerCase()}…</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {games.map((g) => (
            <GameCard
              key={g.id}
              game={g}
              onFavorite={() => favorite(g)}
              favorited={isFavorited(g)}
              onPlayed={markPlayed}
              kids={kids}
            />
          ))}
        </div>
      )}

      <div className="text-[11px] text-slate-400 text-center pt-2 flex items-center justify-center gap-1">
        <Sparkles className="w-3 h-3" /> Klicka <Heart className="w-3 h-3 inline text-rose-400" /> för att spara favoriter · <RefreshCw className="w-3 h-3 inline" /> genererar nya
      </div>
    </div>
  );
}

function KidsSettingsModal({ initial, onClose, onSaved }) {
  const [kids, setKids] = useState(initial.length > 0 ? initial : [{ name: '', age: 6 }]);
  const [busy, setBusy] = useState(false);

  const addKid = () => setKids((k) => (k.length < 10 ? [...k, { name: '', age: 6 }] : k));
  const remove = (i) => setKids((k) => k.filter((_, idx) => idx !== i));
  const update = (i, field, val) => setKids((k) => k.map((x, idx) => (idx === i ? { ...x, [field]: val } : x)));

  const save = async () => {
    const cleaned = kids.filter((k) => (k.name || '').trim().length > 0);
    setBusy(true);
    try {
      const { data } = await axios.put(
        `${API}/family-play/kids`,
        { kids: cleaned.map((k) => ({ name: k.name.trim(), age: parseInt(k.age, 10) || 0 })) },
        { withCredentials: true },
      );
      onSaved(data.kids || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Kunde inte spara');
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[110] bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl max-w-md w-full max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="kids-settings-modal">
        <div className="p-4 border-b border-slate-100 bg-gradient-to-r from-orange-50 to-amber-50 flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase font-bold text-orange-700 tracking-wider">Familj</div>
            <div className="font-bold text-orange-900">Barnens åldrar 🧒👧</div>
          </div>
          <button type="button" onClick={onClose} className="text-slate-400 hover:text-slate-700"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-4 space-y-3">
          <p className="text-xs text-slate-600">
            Lägg till dina barn så anpassas alla lekar till deras åldersspann automatiskt.
          </p>
          {kids.map((k, i) => (
            <div key={i} className="flex items-center gap-2">
              <input
                type="text"
                value={k.name}
                onChange={(e) => update(i, 'name', e.target.value)}
                placeholder="Namn"
                maxLength={40}
                data-testid={`kid-name-${i}`}
                className="flex-1 text-sm rounded-lg border border-slate-200 px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-orange-200"
              />
              <input
                type="number"
                min="0"
                max="25"
                value={k.age}
                onChange={(e) => update(i, 'age', e.target.value)}
                data-testid={`kid-age-${i}`}
                className="w-16 text-sm rounded-lg border border-slate-200 px-2 py-1.5 text-center focus:outline-none focus:ring-2 focus:ring-orange-200"
              />
              <span className="text-xs text-slate-500 w-4">år</span>
              <button
                type="button"
                onClick={() => remove(i)}
                className="text-slate-300 hover:text-rose-500"
                aria-label="Ta bort"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
          {kids.length < 10 && (
            <button
              type="button"
              onClick={addKid}
              data-testid="kid-add-btn"
              className="w-full text-xs px-3 py-1.5 rounded-full bg-orange-50 border border-orange-200 text-orange-800 hover:bg-orange-100 inline-flex items-center justify-center gap-1"
            >
              <Plus className="w-3 h-3" /> Lägg till barn
            </button>
          )}
          <button
            type="button"
            onClick={save}
            disabled={busy}
            data-testid="kid-save-btn"
            className="w-full inline-flex items-center justify-center gap-1 px-4 py-2 rounded-full bg-gradient-to-r from-orange-500 to-amber-500 text-white text-sm font-semibold hover:opacity-90 disabled:opacity-50"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
            Spara
          </button>
        </div>
      </div>
    </div>
  );
}

